/**
 * Display formatting. Every function tolerates null, because the analytics API
 * deliberately returns null rather than zero when there is no data to average.
 */

import { propertyDateKey } from '@nbh/shared';
import { propertyTimeZone } from '../config/property';

/**
 * A UTC timestamp as a date and time in property time.
 *
 * Property time, not device time: a manager opening this from another country must
 * read the same clock as the floor. That is also why no component may reach for
 * `toLocaleString()` on its own — the device zone is only right by accident.
 *
 * An unparseable value comes back untouched rather than as "Invalid Date", so a
 * malformed timestamp shows the operator what the API actually sent.
 */
export function formatDateTime(iso: string | null, timeZone: string = propertyTimeZone): string {
  return formatWith(iso, { dateStyle: 'medium', timeStyle: 'short' }, timeZone);
}

/** The same, to the day — for a "retired on" marker where the minute is noise. */
export function formatDate(iso: string | null, timeZone: string = propertyTimeZone): string {
  return formatWith(iso, { dateStyle: 'medium' }, timeZone);
}

/**
 * Day and month alone — "Sep 14" — for a heading that states a period.
 *
 * Property time like every date here, and the year is deliberately absent: this
 * feeds "Service levels — since Sep 14", read on a phone next to five tiles.
 */
export function formatDayMonth(iso: string | null, timeZone: string = propertyTimeZone): string {
  return formatWith(iso, { day: 'numeric', month: 'short' }, timeZone);
}

/**
 * Time of day alone — "14:02" — for the timestamp inside a chat bubble.
 *
 * The bubble carries the time and the day separator above it carries the day, so a
 * thread never repeats the date on every line. `h23` rather than `hour12: false`: the
 * latter renders midnight as "24:02" in some engines, the same quirk `dateParts` works
 * around below.
 */
export function formatTime(iso: string | null, timeZone: string = propertyTimeZone): string {
  return formatWith(iso, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }, timeZone);
}

/**
 * Which day an instant falls on, relative to now, as a chat thread names it.
 *
 * One rule for the thread's day separators and the inbox's right-hand time, so the two
 * can never disagree about what "yesterday" means:
 *
 *   same property day        today
 *   the day before           yesterday
 *   within the last week     the weekday — "Tue, 7 Jul"
 *   older than that          the full date — "7 Jul 2026"
 *
 * Days are property calendar days (`propertyDateKey`), never the device's: a manager
 * abroad reads the same "Yesterday" the floor does. Today and yesterday come back as
 * kinds rather than strings because their words are the locale files' to choose; the
 * two dated forms are rendered here, through the same `Intl` path as every other date.
 */
export type DayLabel =
  | { kind: 'today' }
  | { kind: 'yesterday' }
  | { kind: 'weekday'; label: string }
  | { kind: 'date'; label: string };

export function describeDay(iso: string, now: Date, timeZone: string = propertyTimeZone): DayLabel {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return { kind: 'date', label: iso };

  const daysAgo = calendarDaysBetween(propertyDateKey(at, timeZone), propertyDateKey(now, timeZone));

  if (daysAgo === 0) return { kind: 'today' };
  if (daysAgo === 1) return { kind: 'yesterday' };
  if (daysAgo > 1 && daysAgo < 7) {
    return {
      kind: 'weekday',
      label: formatWith(iso, { weekday: 'short', day: 'numeric', month: 'short' }, timeZone),
    };
  }
  return {
    kind: 'date',
    label: formatWith(iso, { day: 'numeric', month: 'short', year: 'numeric' }, timeZone),
  };
}

/** Whole calendar days from `fromKey` to `toKey`, both `YYYY-MM-DD`; negative if `fromKey` is later. */
function calendarDaysBetween(fromKey: string, toKey: string): number {
  // Calendar keys parsed as UTC dates have no zone offset to push them across midnight,
  // so the difference is exact whatever the device's own zone is doing.
  const from = Date.parse(`${fromKey}T00:00:00Z`);
  const to = Date.parse(`${toKey}T00:00:00Z`);
  return Math.round((to - from) / 86_400_000);
}

function formatWith(
  iso: string | null,
  options: Intl.DateTimeFormatOptions,
  timeZone: string,
): string {
  if (!iso) return '';

  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;

  return new Intl.DateTimeFormat(undefined, { ...options, timeZone }).format(at);
}

/**
 * A UTC timestamp as the `YYYY-MM-DDTHH:mm` an `<input type="datetime-local">` wants,
 * read in property time.
 *
 * The input has no timezone of its own — it shows whatever string it is given and hands
 * one back. Feeding it a raw ISO string would silently display UTC; letting the browser
 * convert would display the device zone. Both are wrong for the same reason
 * `formatDateTime` exists: a manager abroad must set the same deadline the floor reads.
 */
export function toDateTimeLocal(iso: string | null, timeZone: string = propertyTimeZone): string {
  if (!iso) return '';

  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '';

  const { year, month, day, hour, minute } = dateParts(at, timeZone);

  return `${year}-${month}-${day}T${hour}:${minute}`;
}

/**
 * The inverse: a wall-clock string from the input, as the UTC instant it names in
 * property time.
 *
 * Works by reading the string as though it were UTC and then subtracting the zone's
 * offset at that instant. The offset has to be measured at roughly the right moment
 * rather than assumed, or every deadline set in summer is an hour out in winter.
 *
 * Ambiguous during the hour a clock goes back, when one wall time names two instants;
 * this resolves to the first. That is one hour a year on a maintenance deadline, and
 * the alternative is asking the operator which of two identical times they meant.
 */
export function fromDateTimeLocal(
  local: string,
  timeZone: string = propertyTimeZone,
): string | null {
  if (!local) return null;

  const asIfUtc = new Date(`${local}:00.000Z`);
  if (Number.isNaN(asIfUtc.getTime())) return null;

  const { year, month, day, hour, minute } = dateParts(asIfUtc, timeZone);
  const wallClockAsUtc = Date.UTC(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
  );

  return new Date(asIfUtc.getTime() * 2 - wallClockAsUtc).toISOString();
}

/** The wall-clock fields of an instant in a zone, zero-padded. */
function dateParts(at: Date, timeZone: string) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(at);

  const read = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? '00';

  // en-GB renders midnight as "24" rather than "00" in some engines. Left as-is would
  // produce an invalid `T24:00` the input silently rejects, blanking the field.
  const hour = read('hour') === '24' ? '00' : read('hour');

  return {
    year: read('year'),
    month: read('month'),
    day: read('day'),
    hour,
    minute: read('minute'),
  };
}

/** "4 min", "2 h 15 min", "3 d 4 h" — readable at a glance on a phone. */
export function formatDuration(minutes: number | null): string | null {
  if (minutes === null) return null;

  const whole = Math.round(minutes);
  if (whole < 60) return `${whole} min`;

  const hours = Math.floor(whole / 60);
  if (hours < 24) {
    const rest = whole % 60;
    return rest === 0 ? `${hours} h` : `${hours} h ${rest} min`;
  }

  const days = Math.floor(hours / 24);
  const restHours = hours % 24;
  return restHours === 0 ? `${days} d` : `${days} d ${restHours} h`;
}

/**
 * The badge clock: the span between two instants, in badge units — `14m`, `1h 22m`.
 *
 * `sense` picks the direction: 'overdue' measures how far past `deadline` the clock
 * has run, 'remaining' how far it still has to go; whichever way, a span that has
 * not started yet is 0m, never negative. Rounds DOWN, because a badge that says
 * `14m` while the true figure is 14m40s is honest and one that says `15m` is not.
 * No seconds — the queue re-renders on a 30-second interval and a seconds counter
 * would just display its own staleness. No days unit — a fix 25 hours late reads
 * `25h`, because "1d 1h" softens exactly the number the badge exists to shout.
 *
 * Deliberately not `formatDuration`, which SlaPanel has been rendering for a month:
 * changing its rounding or units in place would silently move dashboard figures a
 * manager already reads. Two similarly named functions is the smaller cost.
 */
export function formatSlaSpan(deadline: Date, now: Date, sense: 'overdue' | 'remaining'): string {
  const ms =
    sense === 'overdue' ? now.getTime() - deadline.getTime() : deadline.getTime() - now.getTime();
  const minutes = Math.max(0, Math.floor(ms / 60_000));

  const hours = Math.floor(minutes / 60);
  if (hours === 0) return `${minutes}m`;

  const rest = minutes % 60;
  return rest === 0 ? `${hours}h` : `${hours}h ${rest}m`;
}

/** A 0–1 ratio as a whole percentage. */
export function formatPercent(ratio: number | null): string | null {
  if (ratio === null) return null;
  return `${Math.round(ratio * 100)}%`;
}

/**
 * Minor units to a display string. Currency comes from property config rather than
 * being hardcoded, and money is never stored as a float.
 */
export function formatMoneyMinor(minor: number, currency = 'GBP', locale = 'en-GB'): string {
  return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(minor / 100);
}
